import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { DashboardLayout } from "@/components/ui/dashboard-layout";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  onboardingApi,
  type OnboardingDataResponse,
  type CalendarIntegration,
} from "@/lib/api";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AccountSettingsTab } from "@/components/settings/AccountSettingsTab";
import { queryClient } from "../../lib/queryClient";
import { useAuthStore } from "@/lib/auth";
import { api } from "@/lib/api";
import { CompanyForm } from "@/components/settings/CompanyForm";
import type { CompanyFormData } from "@/types/company";
import { ToneForm, type ToneFormData } from "@/components/settings/ToneForm";
import { HTTPError } from "ky";
import { authApi } from "@/lib/api";
import { useNavigate } from "@tanstack/react-router";
import { UnauthorizedError } from "@/lib/api";
import { ProviderManager } from "@/components/providers/ProviderManager";
import "@/lib/providers/init"; // Initialize providers
// interface ConnectionForm {
//   apiKey: string;
//   workspace: string;
//   isActive: boolean;
//   showApiKey: boolean;
//   autoReply: boolean;
//   minReplyDelaySeconds: number | null;
//   maxReplyDelaySeconds: number | null;
// }
export const Route = createFileRoute("/settings/")({
  component: Settings,
  head: () => {
    return {
      meta: [
        {
          nofollow: true,
        },
        {
          noindex: true,
        },
        {
          title: "Settings | aiEmailAgent",
        },
        {
          description: "Settings",
        },
      ],
    };
  },
});

/**
 * Settings component manages various application settings organized into tabs.
 * It fetches connection, company, and tone data, handles state management,
 * and orchestrates updates via API calls. It delegates the rendering of
 * individual tab content to specialized child components.
 * @returns {React.ReactElement} The rendered Settings page component.
 */
function Settings() {
  // State for active tab and loading
  const [activeTab, setActiveTab] = useState<
    "account" | "salesforce" | "instantly" | "company" | "tone"
  >("account");
  const [isAuthInitialized, setIsAuthInitialized] = useState(false);

  // Get current user and auth state from auth store
  const user = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const token = useAuthStore((state) => state.token);
  const validateAuth = useAuthStore((state) => state.validateAuth);
  const refreshUser = useAuthStore((state) => state.refreshUser);
  const navigate = useNavigate();

  // Validate auth and refresh user data when component mounts
  useEffect(() => {
    const initAuth = async () => {
      try {
        const isValid = await validateAuth();
        if (isValid) {
          await refreshUser();
        } else {
          console.warn("Auth is invalid, redirecting to login...");
          navigate({
            to: "/auth/login",
            search: { redirectTo: window.location.pathname },
          });
          return;
        }
        setIsAuthInitialized(true);
      } catch (error) {
        if (error instanceof UnauthorizedError) {
          toast.error("Authentication failed", {
            description: "Please log in again.",
          });
          navigate({
            to: "/auth/login",
            search: { redirectTo: window.location.pathname },
          });
        } else {
          toast.error("An error occurred", {
            description: "Please try again later.",
          });
        }
      }
    };
    initAuth();
  }, [validateAuth, refreshUser, navigate]);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (isAuthInitialized && !isAuthenticated) {
      console.warn(
        "Not authenticated after initialization, redirecting to login...",
      );
      navigate({
        to: "/auth/login",
        search: { redirectTo: window.location.pathname },
      });
    }
  }, [isAuthInitialized, isAuthenticated, navigate]);

  // Watch for token changes
  useEffect(() => {
    if (!token && isAuthInitialized) {
      console.warn("Token lost, redirecting to login...");
      navigate({
        to: "/auth/login",
        search: { redirectTo: window.location.pathname },
      });
    }
  }, [token, isAuthInitialized, navigate]);

  // User account settings initialized with current user data
  const [email, setEmail] = useState(user?.email || "");
  const [firstName, setFirstName] = useState(user?.firstName || "");
  const [lastName, setLastName] = useState(user?.lastName || "");
  const [company, setCompany] = useState(user?.company || "");
  const [jobTitle, setJobTitle] = useState(user?.jobTitle || "");

  // Update account fields when user data changes
  useEffect(() => {
    if (user) {
      setEmail(user.email);
      setFirstName(user.firstName || "");
      setLastName(user.lastName || "");
      setCompany(user.company || "");
      setJobTitle(user.jobTitle || "");
    }
  }, [user]);

  // // Fetch existing connections
  // const {
  //   data: connections,
  //   isLoading: _isLoadingConnections,
  //   error: _connectionsError,
  //   refetch: _refetchConnections,
  // } = useQuery({
  //   queryKey: ["connections"],
  //   queryFn: connectionsApi.getConnections,
  // });

  // Memoize or derive connections within useEffect to avoid re-computation on every render
  // const [salesforceConnection, setSalesforceConnection] = useState<
  //   Connection | undefined
  // >(undefined);
  // const [instantlyConnection, setInstantlyConnection] = useState<
  //   Connection | undefined
  // >(undefined);

  // // Connection form states
  // const [salesforceForm, setSalesforceForm] = useState<ConnectionForm>({
  //   apiKey: "",
  //   workspace: "",
  //   isActive: true,
  //   showApiKey: false,
  //   autoReply: false,
  //   minReplyDelaySeconds: null,
  //   maxReplyDelaySeconds: null,
  // });

  // const [instantlyForm, setInstantlyForm] = useState<ConnectionForm>({
  //   apiKey: "",
  //   workspace: "",
  //   isActive: true,
  //   showApiKey: false,
  //   autoReply: false,
  //   minReplyDelaySeconds: null,
  //   maxReplyDelaySeconds: null,
  // });

  // // Update connection states when connections are loaded
  // useEffect(() => {
  //   if (connections) {
  //     const salesforce = connections.find((c) => c.sourceType === "salesforge");
  //     const instantly = connections.find((c) => c.sourceType === "instantly");

  //     // setSalesforceConnection(salesforce);
  //     // setInstantlyConnection(instantly);

  //     // Update form states with existing connection data
  //     if (salesforce) {
  //       setSalesforceForm({
  //         apiKey: salesforce.apiKey || "",
  //         workspace: salesforce.workspace || "",
  //         isActive: salesforce.isActive ?? true,
  //         showApiKey: false,
  //         autoReply: salesforce.autoReply ?? false,
  //         minReplyDelaySeconds: salesforce.minReplyDelaySeconds ?? null,
  //         maxReplyDelaySeconds: salesforce.maxReplyDelaySeconds ?? null,
  //       });
  //     }

  //     if (instantly) {
  //       setInstantlyForm({
  //         apiKey: instantly.apiKey || "",
  //         workspace: instantly.workspace || "",
  //         isActive: instantly.isActive ?? true,
  //         showApiKey: false,
  //         autoReply: instantly.autoReply ?? false,
  //         minReplyDelaySeconds: instantly.minReplyDelaySeconds ?? null,
  //         maxReplyDelaySeconds: instantly.maxReplyDelaySeconds ?? null,
  //       });
  //     }
  //   }
  // }, [connections]);

  // // Test connection mutation
  // const testConnectionMutation = useMutation({
  //   mutationFn: connectionsApi.testConnection,
  //   onSuccess: (data) => {
  //     if (data.success) {
  //       toast.success("Connection test successful", {
  //         description: data.message,
  //       });
  //     } else {
  //       toast.error("Connection test failed", {
  //         description: data.error || data.message,
  //       });
  //     }
  //   },
  //   onError: (error: any) => {
  //     toast.error("Connection test failed", {
  //       description:
  //         error.message || "Please check your credentials and try again",
  //     });
  //   },
  // });

  // // Save connection mutation
  // const saveConnectionMutation = useMutation({
  //   mutationFn: async (data: {
  //     sourceType: SourceType;
  //     connection: Partial<CreateConnection>;
  //   }) => {
  //     const existingConnection = connections?.find(
  //       (c) => c.sourceType === data.sourceType,
  //     );
  //     if (existingConnection?.id) {
  //       // Ensure we only pass updateable fields
  //       const updateData: UpdateConnection = {
  //         apiKey: data.connection.apiKey,
  //         workspace: data.connection.workspace,
  //         isActive: data.connection.isActive,
  //         name: null, // Set to null if not provided
  //         // Include auto-reply settings
  //         autoReply: data.connection.autoReply,
  //         minReplyDelaySeconds: data.connection.minReplyDelaySeconds,
  //         maxReplyDelaySeconds: data.connection.maxReplyDelaySeconds,
  //       };
  //       return connectionsApi.updateConnection(
  //         existingConnection.id,
  //         updateData,
  //       );
  //     } else {
  //       // Ensure required fields for creation are present
  //       const createData: CreateConnection = {
  //         sourceType: data.sourceType,
  //         apiKey: data.connection.apiKey ?? "", // Provide default empty string if undefined
  //         workspace: data.connection.workspace ?? "", // Provide default empty string if undefined
  //         isActive: data.connection.isActive ?? true, // Default to true if undefined
  //         name: null, // Set to null if not provided
  //         // Include auto-reply settings
  //         autoReply: data.connection.autoReply ?? false,
  //         minReplyDelaySeconds: data.connection.minReplyDelaySeconds ?? null,
  //         maxReplyDelaySeconds: data.connection.maxReplyDelaySeconds ?? null,
  //       };
  //       return connectionsApi.createConnection(createData);
  //     }
  //   },
  //   onSuccess: () => {
  //     toast.success("Connection settings saved successfully");
  //     refetchConnections(); // Refetch connections list after save
  //   },
  //   onError: (error: any) => {
  //     toast.error("Failed to save connection settings", {
  //       description: error.message || "Please try again",
  //     });
  //   },
  // });

  // // Validation functions - kept here as they use the form state directly
  // const validateConnectionForm = (
  //   formData: typeof salesforceForm | typeof instantlyForm,
  //   sourceTypeName: string, // Add friendly name for messages
  // ) => {
  //   const errors: string[] = [];
  //   if (!formData.apiKey.trim()) {
  //     errors.push(`${sourceTypeName} API Key is required`);
  //   }
  //   if (!formData.workspace.trim()) {
  //     errors.push(`${sourceTypeName} Workspace is required`);
  //   }
  //   return errors;
  // };

  // Test connection handler
  // const handleTestConnection = async (sourceType: SourceType) => {
  //   const formData =
  //     sourceType === "salesforge" ? salesforceForm : instantlyForm;
  //   const sourceTypeName =
  //     sourceType === "salesforge" ? "Salesforce" : "Instantly";
  //   const errors = validateConnectionForm(formData, sourceTypeName);

  //   if (errors.length > 0) {
  //     toast.error("Validation Error", {
  //       description: errors.join("; "), // Use semicolon or similar for clarity
  //     });
  //     return;
  //   }

  //   // Ensure required fields are not undefined before sending
  //   if (formData.apiKey && formData.workspace) {
  //     await testConnectionMutation.mutateAsync({
  //       sourceType,
  //       apiKey: formData.apiKey,
  //       workspace: formData.workspace,
  //       isActive: formData.isActive,
  //       name: null,
  //       autoReply: formData.autoReply ?? false,
  //       minReplyDelaySeconds: formData.minReplyDelaySeconds ?? null,
  //       maxReplyDelaySeconds: formData.maxReplyDelaySeconds ?? null,
  //     });
  //   } else {
  //     toast.error("Missing Information", {
  //       description:
  //         "API Key and Workspace must be provided to test the connection.",
  //     });
  //   }
  // };

  // // Save connection handler
  // const handleSaveConnection = async (sourceType: SourceType) => {
  //   const formData =
  //     sourceType === "salesforge" ? salesforceForm : instantlyForm;
  //   const sourceTypeName =
  //     sourceType === "salesforge" ? "Salesforce" : "Instantly";
  //   const errors = validateConnectionForm(formData, sourceTypeName);

  //   if (errors.length > 0) {
  //     toast.error("Validation Error", {
  //       description: errors.join("; "),
  //     });
  //     return;
  //   }

  //   // Prepare connection data, ensuring defaults for potentially undefined fields if creating
  //   const connectionData: Partial<Omit<Connection, "id" | "sourceType">> = {
  //     apiKey: formData.apiKey,
  //     workspace: formData.workspace,
  //     isActive: formData.isActive,
  //     autoReply: formData.autoReply ?? false,
  //     minReplyDelaySeconds: formData.autoReply
  //       ? (formData.minReplyDelaySeconds ?? 60)
  //       : null,
  //     maxReplyDelaySeconds: formData.autoReply
  //       ? (formData.maxReplyDelaySeconds ?? 300)
  //       : null,
  //   };

  //   await saveConnectionMutation.mutateAsync({
  //     sourceType,
  //     connection: connectionData,
  //   });
  // };

  // Initialize company form with default values
  const [companyForm, setCompanyForm] = useState<CompanyFormData>({
    companyName: "",
    companyDescription: "",
    companyUrl: "",
    hyperlinkCompanyName: false,
    productDetails: "",
    targetAudience: "",
    sellingPoints: "",
    commonObjections: "",
    preferredIntent: "MEETING_FIRST",
  });

  // Initialize tone preference with default values
  const [toneForm, setToneForm] = useState<ToneFormData>({
    tonePreference: "professional",
    customToneDescription: "",
  });

  // Fetch onboarding data
  const { data: onboardingData, isLoading: isLoadingOnboarding } =
    useQuery<OnboardingDataResponse>({
      queryKey: ["onboarding"],
      enabled: isAuthenticated,
      queryFn: async () => {
        try {
          const response = await api.get("onboarding/data").json();
          return response as OnboardingDataResponse;
        } catch (error) {
          if (error instanceof HTTPError && error.response.status === 404) {
            // Return empty onboarding data if not found
            return {
              success: true,
              data: {
                status: {
                  userId: 0,
                  currentStep: "welcome",
                  isComplete: false,
                  progress: 0,
                },
                companyContext: null,
                tonePreferences: null,
                calendarIntegration: null,
                salesforceConnection: null,
                instantlyConnection: null,
              },
            };
          }
          throw error;
        }
      },
      retry: (failureCount, error) => {
        if (error instanceof HTTPError) {
          // Don't retry on 404 Not Found or 401 Unauthorized
          if (error.response.status === 404 || error.response.status === 401) {
            return false;
          }
        }
        // Retry up to 3 times for other errors
        return failureCount < 3;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    });

  // Handle onboarding error
  useEffect(() => {
    if (onboardingData?.success === false) {
      toast.error("Failed to load settings", {
        description: "Please try refreshing the page.",
      });
    }
  }, [onboardingData?.success]);

  // Update form states when onboarding data is loaded
  useEffect(() => {
    if (onboardingData?.success && onboardingData?.data) {
      const { companyContext, tonePreferences } = onboardingData.data;

      // Update company form if data exists
      if (companyContext) {
        try {
          // Map API response fields to form fields
          const companyFormData = {
            companyName: companyContext.companyName || "",
            companyDescription: companyContext.companyDescription || "",
            companyUrl: companyContext.companyUrl || "",
            hyperlinkCompanyName: companyContext.hyperlinkCompanyName ?? false,
            productDetails: companyContext.productDetails || "",
            targetAudience: companyContext.targetAudience || "",
            sellingPoints: companyContext.sellingPoints || "",
            commonObjections: companyContext.commonObjections || "",
            preferredIntent: companyContext.preferredIntent || "MEETING_FIRST",
          };

          // Validate required fields are present
          if (
            companyFormData.companyName &&
            companyFormData.companyDescription
          ) {
            setCompanyForm(companyFormData);
          }
        } catch (error) {
          toast.error("Failed to load company data", {
            description: "Please try refreshing the page.",
          });
        }
      }

      // Update tone form if data exists
      if (tonePreferences) {
        try {
          const toneFormData = {
            tonePreference: tonePreferences.tonePreference || "professional",
            customToneDescription: tonePreferences.customToneDescription || "",
          };

          setToneForm(toneFormData);
        } catch (error) {
          toast.error("Failed to load tone preferences", {
            description: "Please try refreshing the page.",
          });
        }
      }
    }
  }, [onboardingData]);

  // Company settings mutation
  const saveCompanyMutation = useMutation({
    mutationFn: onboardingApi.saveCompanyContext,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["onboarding"] });
      toast.success("Company settings saved successfully");
    },
    onError: (_) => {
      toast.error("Failed to save company settings", {
        description: "Please try again.",
      });
    },
  });

  // Tone settings mutation
  const saveToneMutation = useMutation({
    mutationFn: onboardingApi.saveTonePreferences,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["onboarding"] });
      toast.success("Tone preferences saved successfully");
    },
    onError: (error) => {
      console.error("Error saving tone settings:", error);
      toast.error("Failed to save tone preferences", {
        description: "Please try again.",
      });
    },
  });

  const handleSaveCompany = async (form: CompanyFormData) => {
    await saveCompanyMutation.mutateAsync(form);
  };

  const handleSaveTone = async (form: ToneFormData) => {
    await saveToneMutation.mutateAsync(form);
  };

  // Profile update mutation
  const updateProfileMutation = useMutation({
    mutationFn: authApi.updateProfile,
    onSuccess: (updatedUser) => {
      // Update auth store with new user data
      useAuthStore.getState().setUser(updatedUser);
      toast.success("Profile updated successfully");
    },
    onError: (error) => {
      console.error("Error updating profile:", error);
      toast.error("Failed to update profile", {
        description: "Please try again.",
      });
    },
  });

  // Handle profile update
  const handleSaveAccount = async () => {
    await updateProfileMutation.mutateAsync({
      firstName,
      lastName,
      company,
      jobTitle,
    });
  };

  // Password change mutation
  const changePasswordMutation = useMutation({
    mutationFn: authApi.changePassword,
    onSuccess: () => {
      toast.success("Password changed successfully");
    },
    onError: (error: any) => {
      console.error("Error changing password:", error);
      toast.error("Failed to change password", {
        description: error.message || "Please try again.",
      });
    },
  });

  // Handle password change
  const handlePasswordChange = async (
    currentPassword: string,
    newPassword: string,
  ) => {
    await changePasswordMutation.mutateAsync({ currentPassword, newPassword });
  };

  // Calendar settings state
  const [calendarForm, setCalendarForm] = useState<CalendarIntegration>({
    calendarType: "",
    bookingLink: "",
  });

  // Update calendar fields when onboarding data changes
  useEffect(() => {
    if (onboardingData?.data?.calendarIntegration) {
      setCalendarForm(onboardingData.data.calendarIntegration);
    }
  }, [onboardingData]);

  // Calendar settings mutation
  const saveCalendarMutation = useMutation({
    mutationFn: onboardingApi.saveCalendarIntegration,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["onboarding"] });
      toast.success("Calendar settings saved successfully");
    },
    onError: (error) => {
      console.error("Error saving calendar settings:", error);
      toast.error("Failed to save calendar settings", {
        description: "Please try again.",
      });
    },
  });

  const handleSaveCalendar = async () => {
    await saveCalendarMutation.mutateAsync(calendarForm);
  };

  // Show loading state
  if (isLoadingOnboarding) {
    return (
      <DashboardLayout title="Settings" activePath="/settings">
        <div className="p-6">
          <div className="mx-auto max-w-4xl">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading settings...</p>
              </div>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }
  return (
    <DashboardLayout title="Settings" activePath="/settings">
      <div className="p-6">
        <div className="mx-auto max-w-4xl">
          <Tabs
            value={activeTab}
            onValueChange={(value) => setActiveTab(value as typeof activeTab)}
            className="w-full"
          >
            <TabsList className="mb-8 grid w-full grid-cols-4">
              <TabsTrigger value="account">Account</TabsTrigger>
              <TabsTrigger value="connections">Connections</TabsTrigger>
              <TabsTrigger value="company">Company</TabsTrigger>
              <TabsTrigger value="tone">Tone</TabsTrigger>
            </TabsList>

            <TabsContent value="account">
              <AccountSettingsTab
                key={user?.id}
                firstName={firstName}
                setFirstName={setFirstName}
                lastName={lastName}
                setLastName={setLastName}
                email={email}
                handleSaveAccount={handleSaveAccount}
                handlePasswordChange={handlePasswordChange}
                isLoading={updateProfileMutation.isPending}
                isPasswordChangeLoading={changePasswordMutation.isPending}
                calendarForm={calendarForm}
                setCalendarForm={setCalendarForm}
                handleSaveCalendar={handleSaveCalendar}
                saveCalendarMutation={saveCalendarMutation}
              />
            </TabsContent>

            <TabsContent value="connections">
              <ProviderManager />
            </TabsContent>

            <TabsContent value="company">
              <CompanyForm
                form={companyForm}
                setForm={setCompanyForm}
                onSubmit={handleSaveCompany}
                isLoading={saveCompanyMutation.isPending}
              />
            </TabsContent>

            <TabsContent value="tone">
              <ToneForm
                form={toneForm}
                setForm={setToneForm}
                onSubmit={handleSaveTone}
                isLoading={saveToneMutation.isPending}
              />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </DashboardLayout>
  );
}
